import type { ReadAloud } from '@mystiq/contracts';
import { sameOrigin } from '../api/client.ts';

export type ReadingHandlers = {
  /** A part (0-based, of the plan's parts) started playing. */
  onPart(index: number): void;
  onEnd(): void;
  onError(message: string): void;
};

export interface Reading {
  start(): void;
  pause(): void;
  resume(): void;
  stop(): void;
  setRate(rate: number): void;
}

/** The server's reason a part could not be played (e.g. the daily allowance), read from its answer. */
async function reasonFor(url: string): Promise<string> {
  try {
    const response = await fetch(url, { headers: { range: 'bytes=0-0' } });
    const data = (await response.json()) as { error?: { message?: string } };
    return data.error?.message ?? 'This part could not be played.';
  } catch {
    return 'This part could not be played.';
  }
}

/**
 * Plays a read-aloud plan's audio parts one after another. While one part
 * plays, the next is already loading (its audio is made then, on the server),
 * so the voice carries on with no long gap.
 */
class SpokenParts implements Reading {
  readonly #parts: Array<{ url: string }>;
  readonly #handlers: ReadingHandlers;
  #rate: number;
  #index = -1;
  #current: HTMLAudioElement | null = null;
  #next: HTMLAudioElement | null = null;
  #stopped = false;

  constructor(parts: Array<{ url: string }>, rate: number, handlers: ReadingHandlers) {
    this.#parts = parts;
    this.#rate = rate;
    this.#handlers = handlers;
  }

  #load(index: number): HTMLAudioElement {
    const audio = new Audio(sameOrigin(this.#parts[index]!.url));
    audio.preload = 'auto';
    audio.defaultPlaybackRate = this.#rate;
    audio.playbackRate = this.#rate;
    return audio;
  }

  #play(index: number) {
    if (this.#stopped) return;
    const audio = this.#next && index === this.#index + 1 ? this.#next : this.#load(index);
    this.#index = index;
    this.#current = audio;
    this.#next = index + 1 < this.#parts.length ? this.#load(index + 1) : null;
    audio.playbackRate = this.#rate;
    audio.onended = () => (index + 1 < this.#parts.length ? this.#play(index + 1) : this.#handlers.onEnd());
    audio.onerror = () => {
      if (this.#stopped) return;
      void reasonFor(audio.src).then((message) => this.#handlers.onError(message));
    };
    this.#handlers.onPart(index);
    void audio.play().catch((error: Error) => {
      if (error.name !== 'AbortError' && !this.#stopped) this.#handlers.onError('The browser did not start playing. Press Listen again.');
    });
  }

  start() {
    this.#play(0);
  }
  pause() {
    this.#current?.pause();
  }
  resume() {
    void this.#current?.play().catch(() => undefined);
  }
  stop() {
    this.#stopped = true;
    for (const audio of [this.#current, this.#next]) {
      if (!audio) continue;
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
  }
  setRate(rate: number) {
    this.#rate = rate;
    for (const audio of [this.#current, this.#next]) if (audio) audio.playbackRate = rate;
  }
}

/** Sentences of at most about 200 characters: some browsers stop long spoken passages part-way. */
function sentences(text: string): string[] {
  const out: string[] = [];
  for (const piece of text.match(/[^.!?।。！？\n]+[.!?।。！？]*["'”’)\]]*|\n+/g) ?? []) {
    let rest = piece.trim();
    while (rest.length > 200) {
      const space = rest.lastIndexOf(' ', 200);
      const cut = space > 0 ? space : 200;
      out.push(rest.slice(0, cut).trim());
      rest = rest.slice(cut).trim();
    }
    if (rest) out.push(rest);
  }
  return out;
}

/** The device's own voice (speechSynthesis), used when no speech service is set up. */
class DeviceVoice implements Reading {
  readonly #queue: Array<{ part: number; text: string }>;
  readonly #handlers: ReadingHandlers;
  #rate: number;
  #position = 0;
  #stopped = false;

  constructor(parts: Array<{ text: string }>, rate: number, handlers: ReadingHandlers) {
    this.#queue = parts.flatMap((part, index) => sentences(part.text).map((text) => ({ part: index, text })));
    this.#rate = rate;
    this.#handlers = handlers;
  }

  #speak() {
    if (this.#stopped) return;
    const next = this.#queue[this.#position];
    if (!next) {
      this.#handlers.onEnd();
      return;
    }
    if (this.#position === 0 || this.#queue[this.#position - 1]!.part !== next.part) this.#handlers.onPart(next.part);
    const utterance = new SpeechSynthesisUtterance(next.text);
    utterance.rate = this.#rate;
    utterance.lang = document.documentElement.lang || navigator.language;
    utterance.onend = () => {
      this.#position++;
      this.#speak();
    };
    utterance.onerror = (event) => {
      if (!this.#stopped && event.error !== 'interrupted' && event.error !== 'canceled') this.#handlers.onError("This device's voice could not read the text.");
    };
    speechSynthesis.speak(utterance);
  }

  start() {
    speechSynthesis.cancel();
    this.#speak();
  }
  pause() {
    speechSynthesis.pause();
  }
  resume() {
    speechSynthesis.resume();
  }
  stop() {
    this.#stopped = true;
    speechSynthesis.cancel();
  }
  setRate(rate: number) {
    // A spoken sentence keeps its speed; the next one uses the new speed.
    this.#rate = rate;
  }
}

/** Plays a plan: with the workspace's speech service, or the device's own voice when none is set up. */
export function readingFor(plan: ReadAloud, rate: number, handlers: ReadingHandlers): Reading | null {
  if (plan.provider !== 'browser') {
    const parts = plan.parts.filter((part): part is typeof part & { url: string } => part.url !== null);
    return new SpokenParts(parts, rate, handlers);
  }
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null;
  return new DeviceVoice(plan.parts, rate, handlers);
}
